import { useState, useEffect, Suspense, useRef } from "react";
import GenericTable from "../../../common/components/ui/table/GenericTable";
import {
  TableFiltersState,
  TableFilters,
} from "../../../common/components/ui/table/TableFilters";
import TableFooter from "../../../common/components/ui/table/TableFooter";
import { useToastContext } from "../../../common/components/ui/toast/toast.context";
import { getCompanyId } from "../../../common/services/company.api";
import { useCompanyStore } from "../../../common/states/store/Company";
import {
  ASSET_CATEGORY_LABELS,
  DEPARTMENT_LABELS,
  EnumAssetCategory,
  EnumAssetStatus,
  EnumDepartment,
  IinventoryManagement,
  EnumDocumentStatus,
} from "../../../types/inventory-management.type";
import { ModalCreateInventoryManagement } from "./ModalCreateMaintenanceSchedules";
import { ModalEditInventoryManagement } from "./ModalEditInventoryManagement";
import { ModalDeleteInventoryManagement } from "./ModalDeleteInventoryManagement";
import { ModalLinkEmployeer } from "./ModalLinkEmployeer";
import { ModalDocumentUpload } from "./ModalUploadDocument";
import {
  deleteInventoryManagement,
  downloadSignedDocument,
  getInventoryManagementPaged,
} from "../../../common/services/Inventory-management.api";
import { Modal } from "flowbite-react";
import { PrintableDocument } from "@/common/components/ui/PrintableDocument";
import { Header } from "./pdf/Header";
import {
  CompanySection,
  EmployeeSection,
  EquipmentSection,
  SignatureSection,
  TermsSection,
} from "./pdf/Body";
import { useReactToPrint } from "react-to-print";

import { fetchAssetTermsDocumentData, useEmployeers } from "../hooks";

import { IEmployee } from "@/types/employee.type";
import { IProduct } from "@/types/product.types";

const formatGurmetInvetoryManagement = (
  inventoryList: IinventoryManagement[]
) => {
  return inventoryList.map((inventory) => ({
    assetCode: inventory.assetCode || "",
    description: inventory.description || "",
    category: getAssetCategoryLabel(
      (inventory.category as EnumAssetCategory) || "",
    ),
    currentStatus: getAssetStatus(
      (inventory.currentStatus as EnumAssetStatus) || "",
    ),
    departmentEnum: getEnumDepartment(
      (inventory.departmentEnum as EnumDepartment) || "",
    ),
    documentStatus: getDocumentStatus(
      (inventory.documentStatus as EnumDocumentStatus) || "",
    ),
    employeerId: inventory.employeerId || "",
    _original: inventory,
  }));
};

// eslint-disable-next-line react-refresh/only-export-components
export function getAssetCategoryLabel(category: EnumAssetCategory): string {
  return ASSET_CATEGORY_LABELS[category] ?? "Categoria desconhecida";
}

// eslint-disable-next-line react-refresh/only-export-components
export const ASSET_STATUS_LABELS = {
  [EnumAssetStatus.ACTIVE]: "Em uso",
  [EnumAssetStatus.DISPOSED]: "Baixado/descartado",
  [EnumAssetStatus.TRANSFERRED]: "Transferido entre locais",
  [EnumAssetStatus.UNDER_MAINTENANCE]: "Em manutenção",
};

// eslint-disable-next-line react-refresh/only-export-components
export const DOCUMENT_STATUS_LABELS = {
  [EnumDocumentStatus.GENERATED]: "Gerado",
  [EnumDocumentStatus.SIGNED]: "Assinado",
};

// eslint-disable-next-line react-refresh/only-export-components
export function getDocumentStatus(status: string) {
  return DOCUMENT_STATUS_LABELS[status as EnumDocumentStatus] || status;
}

// eslint-disable-next-line react-refresh/only-export-components
export function getAssetStatus(status: EnumAssetStatus) {
  return ASSET_STATUS_LABELS[status] || status;
}

// eslint-disable-next-line react-refresh/only-export-components
export function getEnumDepartment(status: EnumDepartment) {
  return DEPARTMENT_LABELS[status] || status;
}

const headers = {
  assetCode: "Código do Ativo",
  description: "Descrição",
  category: "Categoria",
  currentStatus: "Status do Ativo",
  documentStatus: "Status do Documento",
  departmentEnum: "Departamento",
  employeerId: "Colaborador",
};

export function TableInventoryManagement() {
  const company = useCompanyStore();
  const companyId = getCompanyId();
  const { notifyError } = useToastContext();
  const [page, setPage] = useState<number>(1);
  const [data, setData] = useState({ itemsPerPage: 10, totalItems: 0 });
  const [action, setAction] = useState<{ eventName: string; data?: unknown }>({
    eventName: "",
    data: undefined,
  });
  const isAction = (eventName: string) => action.eventName === eventName;
  const cleanActionData = () => setAction({ eventName: "", data: undefined });
  const [employeer, setEmployeer] = useState<IEmployee[]>([]);
  const [inventoryManagementDoc, setInventoryManagementDoc] =
    useState<IinventoryManagement | null>(null);
  const [productAsset, setProductAsset] = useState<IProduct[]>([]);
  const contentRef = useRef<HTMLDivElement>(null);

  const { data: employeers } = useEmployeers();

  const handlePrint = useReactToPrint({
    content: () => contentRef.current,
    documentTitle: "Termo de Entrega de Equipamento",
    removeAfterPrint: true,
    pageStyle: `
        @page { 
          margin-left: 2cm; 
          margin-right: 2cm; 
          size: A4 portrait;
        }
        @media print {
          body { 
            -webkit-print-color-adjust: exact; 
            print-color-adjust: exact;
            margin: 0;
            padding: 0;
          }
          .print-container { 
            width: 100%; 
            margin: 0;
            padding: 0;
          }
          .print-content {
            padding: 0;
            margin: 0;
            width: 100%;
          }
        }
      `,
  });

  const generateAndPrint = async (inventoryManagement) => {
    const dataDocument = await fetchAssetTermsDocumentData({
      inventoryManagement,
    });
    setEmployeer([dataDocument.employeer]);
    setInventoryManagementDoc(dataDocument.inventory);
    setProductAsset([dataDocument.product]);
    setTimeout(handlePrint, 0);
  };

  // Instância em memória com dois exemplos de ativos imobilizados
  const [inventoryManagement, setInventoryManagement] = useState<
    IinventoryManagement[]
  >([]);

  const [filters, setFilters] = useState<TableFiltersState>({
    search: "",
    status: [],
    filterColumn: "",
    orderBy: "updatedAt",
  });
  const [confirmDelete, setConfirmDelete] = useState<{
    open: boolean;
    item?: IinventoryManagement;
  }>({ open: false, item: undefined });

  async function loadInventoryManagement(filters, page) {
    try {
      setInventoryManagement([]);

      const result = await getInventoryManagementPaged({
        ...filters,
        page: page,
        itemsPerPage: Number(filters.itemsPerPage) || data.itemsPerPage,
      });

      setData({
        itemsPerPage: data.itemsPerPage,
        totalItems: result.totalItems,
      });

      if (!result.ok)
        return notifyError(
          result.message || "Erro ao carregar os ativos imobilizados",
        );

      setInventoryManagement(result.data || []);
    } catch (error) {
      notifyError("Ocorreu um erro ao carregar os ativos");
    }
  }

  useEffect(() => {
    loadInventoryManagement(filters, page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, page, notifyError, companyId, company]);

  const actions = [
    {
      label: "Editar",
      action: (data) => setAction({ eventName: "edit", data: data._original }),
      permission: "edit:renters",
    },
    {
      label: "Excluir",
      action: (data) => setConfirmDelete({ open: true, item: data._original }),
      permission: "edit:renters",
      disabled: (data) => data.documentStatus === "Assinado",
    },
    {
      label: "Víncular Colaborador",
      action: (data) =>
        setAction({ eventName: "addEmployeer", data: data._original }),
      permission: "edit:renters",
    },
    {
      label: "Gerar Documento",
      action: (data) => {
        generateAndPrint(data._original);
      },
      permission: "edit:renters",
      disabled: (data) => data.documentStatus === "Assinado",
    },
    {
      label: "Gerar Documento",
      action: (data) => generateAndPrint(data._original),
      permission: "edit:renters",
      disabled: (data) =>
        data.documentStatus === "Assinado" || !data.employeerId,
    },
    {
      label: "Upload Documento Assinado",
      action: (data) =>
        setAction({ eventName: "uploadDocument", data: data._original }),
      permission: "edit:renters",
      disabled: (data) => data.documentStatus === "Assinado",
    },
    {
      label: "Download Documento",
      action: (data) => downloadSignedDocument(data._original?._id),
      permission: "view:renters",
      disabled: (data) => data.documentStatus !== "Assinado",
    },
  ];

  const employeeName = (employeerId) =>
    employeers?.find((employee) => employee._id === employeerId)?.name || "";

  const handleFilterChange = (newFilters: Partial<TableFiltersState>) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      ...newFilters,
    }));
  };

  return (
    <Suspense>
      <div className="table-section">
        <TableFilters
          dashboardHeader="Ativo imobilizado"
          placeholder="Digite o código ou descrição do seu imobilizado"
          statusOptions={ASSET_STATUS_LABELS}
          newItemLabel="Novo imobilizado"
          onNewItemClick={() =>
            setAction({ eventName: "create", data: undefined })
          }
          onSearch={handleFilterChange}
          entity="maintenance"
          tableData={
            formatGurmetInvetoryManagement(
              inventoryManagement,
            ) as unknown as Record<string, unknown>[]
          }
        />

        <GenericTable
          headers={headers}
          data={
            formatGurmetInvetoryManagement(
              inventoryManagement,
            ) as unknown as Record<string, unknown>[]
          }
          entity="maintenance"
          actions={actions}
          setFilters={({ orderBy }) => {
            setFilters((prevFilters) => ({
              ...prevFilters,
              orderBy,
            }));
          }}
          renders={{
            currentStatus: (currentStatus) => {
              return (
                <span className="flex justify-center items-center gap-1">
                  {currentStatus as string}
                </span>
              );
            },
            employeerId: (employeerId) => {
              return (
                <span className="flex justify-center items-center gap-1">
                  {employeeName(employeerId)}
                </span>
              );
            },
            documentStatus: (documentStatus) => {
              return (
                <span className="flex justify-center items-center gap-1">
                  {getDocumentStatus(documentStatus as string)}
                </span>
              );
            },
          }}
        />
        <TableFooter
          page={page || 1}
          totalItems={data.totalItems || 0}
          itemsPerPage={+(filters.itemsPerPage ?? 10)}
          goToPage={setPage}
        />
        {isAction("create") && (
          <ModalCreateInventoryManagement
            onClose={cleanActionData}
            onSave={(newInventory) => {
              setInventoryManagement((prev) => [...prev, newInventory]);
              cleanActionData();
            }}
          />
        )}

        {isAction("edit") && (
          <ModalEditInventoryManagement
            inventoryManagement={action.data as IinventoryManagement}
            onClose={cleanActionData}
            onSave={(newInventory) => {
              setInventoryManagement((prev) =>
                prev.map((item) =>

                  item._id === newInventory._id ? newInventory : item,
                ),
              );
              cleanActionData();
            }}
            onChange={(newInventory) => {
              setInventoryManagement((prev) =>
                prev.map((item) =>
                  item._id === newInventory._id ? newInventory : item,
                ),
              );
            }}
          />
        )}

        {isAction("delete") && (
          <ModalDeleteInventoryManagement
            inventoryManagement={action.data as IinventoryManagement}
            onClose={cleanActionData}
            onDelete={(newInventory) => {
              setInventoryManagement((prev) =>
                prev.filter((item) => item._id !== newInventory._id),
              );
              cleanActionData();
            }}
          />
        )}
        {isAction("uploadDocument") && (
          <ModalDocumentUpload
            inventoryId={(action.data as IinventoryManagement)._id}
            onClose={cleanActionData}
            equipmentCode={(action?.data as IinventoryManagement)?.assetCode}
            employeeName={employeeName(
              (action?.data as IinventoryManagement).employeerId,
            )}
          />
        )}
        {isAction("addEmployeer") && (
          <ModalLinkEmployeer
            inventoryManagement={action.data as IinventoryManagement}
            onClose={cleanActionData}
          />
        )}
        <PrintableDocument
          contentRef={contentRef}
          header={<Header />}
          sections={[
            {
              id: "employee",
              component: <EmployeeSection employeer={employeer} />,

            },
            { id: "company", component: <CompanySection /> },
            {
              id: "equipment",
              component: (
                <EquipmentSection
                  equipment={productAsset}
                  obs={inventoryManagementDoc?.notes}
                  category={inventoryManagementDoc?.category}
                  serieNumber={inventoryManagementDoc?.serieNumber}
                />
              ),
            },
            { id: "terms", component: <TermsSection /> },
            { id: "signature", component: <SignatureSection /> },
          ]}
        />

        <Modal
          show={confirmDelete.open}
          size="md"
          onClose={() => setConfirmDelete({ open: false, item: undefined })}
          className="rounded-lg"
        >
          <div className="p-6">
            <h2 className="text-xl font-semibold text-[#e44131] mb-2">
              Confirmar Exclusão
            </h2>
            <p className="text-gray-700 mb-6">
              Tem certeza que deseja excluir este item de imobilizado?
            </p>
            <div className="flex justify-end gap-3">
              <button
                className="rounded px-4 py-2 text-sm font-semibold transition bg-gray-200 text-gray-700 hover:bg-gray-300"
                onClick={() =>
                  setConfirmDelete({ open: false, item: undefined })
                }
              >
                Cancelar
              </button>

              <button
                className="rounded px-4 py-2 text-sm font-semibold transition bg-red-600 text-white hover:bg-red-700"
                onClick={async () => {
                  if (!confirmDelete.item) return;
                  const result = await deleteInventoryManagement(
                    confirmDelete.item._id,
                  );
                  if (result.ok) {
                    setInventoryManagement((prev) =>
                      prev.filter(
                        (item) => item._id !== confirmDelete.item!._id,
                      ),
                    );
                  }
                  setConfirmDelete({ open: false, item: undefined });
                }}
              >
                Sim, excluir
              </button>
            </div>
          </div>
        </Modal>
      </div>
    </Suspense>
  );
}
